import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon as BreadcrumbChevron } from '@heroicons/react/24/outline';
import CabWorkbenchChangePanel from './CabWorkbenchChangePanel';
import ChangeApprovalDecisionModal from '../../../components/ChangeApprovalDecisionModal';
import type { CabWorkbenchMeetingNoteEntry, CabWorkbenchMeetingNotesGroup } from '../../../types/cabWorkbench';
import {
  becomeCabWorkbenchHost,
  demoteCabWorkbenchAgendaItem,
  endCabWorkbenchMeeting,
  getCabWorkbenchMeetingDetail,
  nextCabWorkbenchAgendaItem,
  pauseCabWorkbenchAgendaItem,
  resumeCabWorkbenchAgendaItem,
  skipCabWorkbenchAgendaItem,
  startCabWorkbenchMeeting,
  submitCabWorkbenchCabDecision,
} from '../../../services/cabWorkbenchService';
import {
  getAttendanceStatusLabel,
  getAttendeeReasonLabel,
} from '../../../constants/cabAttendeeOptions';

type HeaderTab = 'notes' | 'attendees' | 'conference';
type AgendaListFilter = 'pending' | 'all';

const TEAL = {
  btn: 'bg-teal-700 hover:bg-teal-800 text-white',
  text: 'text-teal-700',
  link: 'text-teal-700 hover:text-teal-900 hover:underline',
  iconBg: 'bg-teal-700',
};

function agendaStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'in_progress':
      return 'In Progress';
    case 'paused':
      return 'Paused';
    case 'complete':
      return 'Completed';
    case 'no_decision':
      return 'No Decision';
    default:
      return status;
  }
}

const CabWorkbenchMeetingDetail: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [headerTab, setHeaderTab] = useState<HeaderTab>('notes');
  const [agendaListFilter, setAgendaListFilter] = useState<AgendaListFilter>('pending');
  const [tick, setTick] = useState(0);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  /** CR selected for read-only detail preview before the meeting starts. */
  const [previewChangeRequestId, setPreviewChangeRequestId] = useState<string | null>(null);

  const {
    data: meeting,
    isLoading,
    isPending,
    isFetching,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['cab-workbench-meeting', meetingId],
    queryFn: () => getCabWorkbenchMeetingDetail(meetingId!),
    enabled: Boolean(meetingId),
    refetchOnMount: 'always',
    // Live Meeting Notes / agenda while meeting is in progress
    refetchInterval: (query) => {
      const d = query.state.data;
      if (
        d?.meetingStarted &&
        !d?.meetingCompleted &&
        d?.status === 'In Progress'
      ) {
        return 4000;
      }
      return false;
    },
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });

  const meetingActive =
    Boolean(meeting?.meetingStarted) && !meeting?.meetingCompleted && meeting?.status === 'In Progress';

  // Clear pre-meeting CR preview once the live workspace takes over
  useEffect(() => {
    if (meeting?.meetingStarted) {
      setPreviewChangeRequestId(null);
    }
  }, [meeting?.meetingStarted]);

  useEffect(() => {
    if (!meetingActive) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [meetingActive]);

  const current = meeting?.currentAgendaItem ?? null;

  const currentRemainingSeconds = useMemo(() => {
    void tick;
    void dataUpdatedAt;
    if (!current) return 0;
    const allotted = (current.allottedMinutes || 5) * 60;
    let elapsed = current.elapsedSeconds || 0;
    // elapsedSeconds from API is live at fetch for in_progress; adjust by wall clock since fetch
    if (current.status === 'in_progress') {
      const ageSec = Math.max(0, Math.floor((Date.now() - dataUpdatedAt) / 1000));
      elapsed += ageSec;
    }
    return Math.max(0, allotted - elapsed);
  }, [current, tick, dataUpdatedAt]);

  const invalidate = async (detail?: typeof meeting) => {
    if (detail) {
      queryClient.setQueryData(['cab-workbench-meeting', meetingId], detail);
    } else {
      await queryClient.invalidateQueries({ queryKey: ['cab-workbench-meeting', meetingId] });
    }
    queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
  };

  const runAction = async (
    action: () => Promise<NonNullable<typeof meeting>>,
    successMessage?: string
  ) => {
    try {
      const detail = await action();
      await invalidate(detail);
      if (successMessage) toast.success(successMessage);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Action failed.');
    }
  };

  const hostMutation = useMutation({
    mutationFn: () => becomeCabWorkbenchHost(meetingId!),
    onSuccess: (d) => invalidate(d),
  });
  const startMutation = useMutation({
    mutationFn: () => startCabWorkbenchMeeting(meetingId!),
    onSuccess: (d) => invalidate(d),
  });
  const endMutation = useMutation({
    mutationFn: () => endCabWorkbenchMeeting(meetingId!),
    onSuccess: (d) => invalidate(d),
  });
  const pauseMutation = useMutation({
    mutationFn: (agendaItemId: string) =>
      pauseCabWorkbenchAgendaItem(meetingId!, agendaItemId),
    onSuccess: (d) => invalidate(d),
  });
  const resumeMutation = useMutation({
    mutationFn: (agendaItemId: string) =>
      resumeCabWorkbenchAgendaItem(meetingId!, agendaItemId),
    onSuccess: (d) => invalidate(d),
  });
  const nextMutation = useMutation({
    mutationFn: (agendaItemId: string) =>
      nextCabWorkbenchAgendaItem(meetingId!, agendaItemId),
    onSuccess: (d) => invalidate(d),
  });
  const demoteMutation = useMutation({
    mutationFn: (agendaItemId: string) =>
      demoteCabWorkbenchAgendaItem(meetingId!, agendaItemId),
    onSuccess: (d) => invalidate(d),
  });
  const skipMutation = useMutation({
    mutationFn: ({ agendaItemId, comments }: { agendaItemId: string; comments?: string }) =>
      skipCabWorkbenchAgendaItem(meetingId!, agendaItemId, comments),
    onSuccess: (d) => invalidate(d),
  });
  const cabDecisionMutation = useMutation({
    mutationFn: ({
      agendaItemId,
      decision,
      comments,
    }: {
      agendaItemId: string;
      decision: 'approved' | 'rejected';
      comments?: string;
    }) => submitCabWorkbenchCabDecision(meetingId!, agendaItemId, decision, comments),
    onSuccess: (d) => {
      invalidate(d);
      queryClient.invalidateQueries({ queryKey: ['workbench-change'] });
      queryClient.invalidateQueries({ queryKey: ['workbench-change-approvals'] });
    },
  });

  const handleApproveSubmit = async (comments: string) => {
    if (!current?.id) return;
    try {
      await cabDecisionMutation.mutateAsync({
        agendaItemId: current.id,
        decision: 'approved',
        comments: comments || undefined,
      });
      setShowApproveModal(false);
      toast.success('Change approved. Moving to next agenda item.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Approval failed.');
    }
  };

  const handleRejectSubmit = async (comments: string) => {
    if (!current?.id) return;
    try {
      await cabDecisionMutation.mutateAsync({
        agendaItemId: current.id,
        decision: 'rejected',
        comments: comments || undefined,
      });
      setShowRejectModal(false);
      toast.success('Change rejected. Moving to next agenda item.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Rejection failed.');
    }
  };

  const handleSkipSubmit = async (comments: string) => {
    if (!current?.id) return;
    try {
      await skipMutation.mutateAsync({
        agendaItemId: current.id,
        comments: comments || undefined,
      });
      setShowSkipModal(false);
      toast.success('Change Request skipped. Moved to end of agenda.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to skip Change Request.');
    }
  };

  const busy =
    hostMutation.isPending ||
    startMutation.isPending ||
    endMutation.isPending ||
    pauseMutation.isPending ||
    resumeMutation.isPending ||
    nextMutation.isPending ||
    demoteMutation.isPending ||
    skipMutation.isPending ||
    cabDecisionMutation.isPending;

  const handleEndMeeting = async () => {
    if (!window.confirm('End this CAB meeting? This will mark the meeting as Completed and send the MOM email to attendees.')) {
      return;
    }
    try {
      const result = await endMutation.mutateAsync();
      await invalidate(result);
      setPreviewChangeRequestId(null);
      setHeaderTab('notes');
      toast.success('Meeting ended.');
      if (result.momEmailSent) {
        toast.success(
          `MOM email sent to ${result.momEmailRecipientCount ?? 'all'} attendee(s).`
        );
      } else {
        toast.error(
          result.momEmailError
            ? `Meeting ended successfully, but the MOM email could not be sent. ${result.momEmailError}`
            : 'Meeting ended successfully, but the MOM email could not be sent. Please retry sending the MOM.',
          { duration: 8000 }
        );
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to end meeting.');
    }
  };

  if (!meetingId) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB WorkBench Meeting</h1>
        <p className="text-gray-600 mb-4">Meeting id is missing from the URL.</p>
        <button
          type="button"
          onClick={() => navigate('/admin/cab-meeting-management/cab-workbench')}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
        >
          Back to WorkBench
        </button>
      </div>
    );
  }

  if (isPending || isLoading || (isFetching && !meeting && !isError)) {
    return <div className="p-6 text-gray-600">Loading meeting...</div>;
  }

  if (isError || !meeting) {
    const apiMessage = (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
    const status = (error as { response?: { status?: number } })?.response?.status;
    const message =
      apiMessage ||
      (status === 401
        ? 'Your session expired. Please sign in again.'
        : status === 403
          ? 'You do not have permission to view this meeting.'
          : status === 404
            ? 'CAB Meeting not found or you are not allocated to this meeting.'
            : (error as Error)?.message || 'Failed to load meeting details.');
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB WorkBench Meeting</h1>
        <p className="text-gray-600 mb-4">{message}</p>
        <button
          type="button"
          onClick={() => navigate('/admin/cab-meeting-management/cab-workbench')}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
        >
          Back to WorkBench
        </button>
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-2 px-4 py-2 text-sm text-white bg-teal-700 rounded-md hover:bg-teal-800"
        >
          Retry
        </button>
      </div>
    );
  }

  const pending = meeting.pendingAgendaItems || [];
  const allAgendaItems = [...(meeting.agendaItems || [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.changeNumber.localeCompare(b.changeNumber)
  );
  const listedAgendaItems = agendaListFilter === 'all' ? allAgendaItems : pending;
  const showActiveWorkspace = meeting.meetingStarted && !meeting.meetingCompleted;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>
            <Link to="/admin/cab-meeting-management/cab-workbench" className="hover:text-indigo-600">
              Home
            </Link>
          </li>
          <li>
            <BreadcrumbChevron className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link to="/admin/cab-meeting-management/cab-workbench" className="hover:text-indigo-600">
              CAB Workbench
            </Link>
          </li>
          <li>
            <BreadcrumbChevron className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium truncate max-w-md">{meeting.name}</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 shrink-0">
              {(meeting.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">{meeting.name}</h1>
              <p className="text-sm text-gray-500">
                Scheduled {meeting.scheduledDurationMinutes} minutes
                {meeting.status ? (
                  <span className="ml-2 text-gray-400">{'\u00B7'} {meeting.status}</span>
                ) : null}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {meeting.showHostButton && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  runAction(
                    () => hostMutation.mutateAsync(),
                    'You are now the meeting host.'
                  )
                }
                className={`px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 ${TEAL.btn}`}
              >
                Host
              </button>
            )}
            {meeting.showStartMeeting && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  runAction(() => startMutation.mutateAsync(), 'Meeting started.')
                }
                className={`px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 ${TEAL.btn}`}
              >
                Start meeting
              </button>
            )}
            {!meeting.meetingStarted &&
              !meeting.showStartMeeting &&
              !meeting.showHostButton && (
                <span className="text-sm font-medium text-gray-600">Meeting will start</span>
              )}

            <nav className="flex items-center gap-4 text-sm" aria-label="Meeting sections">
              <button
                type="button"
                onClick={() => setHeaderTab('notes')}
                className={
                  headerTab === 'notes' ? `font-medium ${TEAL.text}` : 'text-gray-600 hover:text-gray-900'
                }
              >
                Meeting Notes
              </button>
              <button
                type="button"
                onClick={() => setHeaderTab('attendees')}
                className={`inline-flex items-center gap-1.5 ${
                  headerTab === 'attendees'
                    ? `font-medium ${TEAL.text}`
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Attendees
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-gray-700 text-white text-xs">
                  {meeting.attendeeCount}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setHeaderTab('conference')}
                className={
                  headerTab === 'conference'
                    ? `font-medium ${TEAL.text}`
                    : 'text-gray-600 hover:text-gray-900'
                }
              >
                Conference Information
              </button>
            </nav>
          </div>
        </div>

        {/* Body */}
        {!showActiveWorkspace ? (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_1fr]">
            <aside className="border-r border-gray-200 flex flex-col min-h-0">
              <div className="px-3 py-2 border-b border-gray-200 text-sm font-medium text-gray-700">
                Pending Agenda Items
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {(meeting.agendaItems || [])
                  .filter((i) => i.status === 'pending')
                  .map((item) => {
                    const selected = previewChangeRequestId === item.changeRequestId;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setPreviewChangeRequestId(item.changeRequestId);
                          setHeaderTab('notes');
                        }}
                        className={`w-full text-left rounded-md border p-3 bg-white transition-colors ${
                          selected
                            ? 'border-teal-600 ring-1 ring-teal-600'
                            : 'border-gray-200 hover:border-teal-400 hover:bg-teal-50/40'
                        }`}
                        aria-pressed={selected}
                      >
                        <div className="flex gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">{item.changeNumber}</div>
                            <p className="mt-1 text-sm text-gray-700 line-clamp-2">
                              {item.shortDescription}
                            </p>
                          </div>
                          <div className="shrink-0 h-9 w-9 rounded-full bg-gray-100 border flex items-center justify-center text-sm font-medium text-gray-700">
                            {item.allottedMinutes ?? item.sortOrder}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                {(meeting.agendaItems || []).filter((i) => i.status === 'pending').length === 0 && (
                  <p className="p-4 text-sm text-gray-500 text-center">
                    No agenda items available for this meeting.
                  </p>
                )}
              </div>
              <div className="border-t border-gray-200 flex flex-col min-h-[140px] max-h-[40%] shrink-0">
                <MeetingNotesPanel
                  notes={meeting.meetingNotes}
                  notesByChange={meeting.meetingNotesByChange}
                  compact
                />
              </div>
            </aside>
            <section className="flex flex-col min-h-0 overflow-hidden">
              {headerTab === 'attendees' ? (
                <div className="p-4 overflow-auto">
                  <AttendeesTable meeting={meeting} />
                </div>
              ) : headerTab === 'conference' ? (
                <div className="p-4">
                  <ConferenceBlock meeting={meeting} />
                </div>
              ) : meeting.meetingCompleted ? (
                <div className="flex-1 m-4 border border-gray-200 rounded-md flex flex-col min-h-0 overflow-hidden bg-white">
                  <MeetingNotesPanel
                    notes={meeting.meetingNotes}
                    notesByChange={meeting.meetingNotesByChange}
                    noteEntries={meeting.noteEntries}
                    chronological
                  />
                </div>
              ) : previewChangeRequestId ? (
                <CabWorkbenchChangePanel
                  meetingId={meeting.id}
                  changeRequestId={previewChangeRequestId}
                />
              ) : (
                <div className="flex-1 m-4 border border-gray-200 rounded-md flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
                    <div
                      className={`h-16 w-16 rounded-full ${TEAL.iconBg} flex items-center justify-center mb-4`}
                    >
                      <ClockIcon className="h-8 w-8 text-white" />
                    </div>
                    <p className="text-gray-700 max-w-md">
                      Select a Change Request from the list to view its details. Meeting notes
                      appear after Change Requests receive approval decisions.
                    </p>
                    {meeting.showStartMeeting && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          runAction(() => startMutation.mutateAsync(), 'Meeting started.')
                        }
                        className={`mt-4 text-sm font-medium ${TEAL.link} disabled:opacity-50`}
                      >
                        Start meeting
                      </button>
                    )}
                    {meeting.showHostButton && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          runAction(
                            () => hostMutation.mutateAsync(),
                            'You are now the meeting host.'
                          )
                        }
                        className={`mt-4 text-sm font-medium ${TEAL.link} disabled:opacity-50`}
                      >
                        Host
                      </button>
                    )}
                    {!meeting.showStartMeeting && !meeting.showHostButton && (
                      <p className={`mt-4 text-sm font-medium ${TEAL.text}`}>Meeting will start</p>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        ) : headerTab === 'attendees' ? (
          <div className="flex-1 overflow-auto p-4">
            <AttendeesTable meeting={meeting} />
          </div>
        ) : headerTab === 'conference' ? (
          <div className="flex-1 overflow-auto p-4">
            <ConferenceBlock meeting={meeting} />
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[340px_1fr]">
            {/* Left: current + pending + live notes */}
            <aside className="border-r border-gray-200 flex flex-col min-h-0 bg-white">
              <div className="p-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-gray-900">Current Agenda Item</h2>
                  {meeting.showEndMeeting && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleEndMeeting}
                      className={`px-3 py-1 text-sm font-medium rounded-md disabled:opacity-50 ${TEAL.btn}`}
                    >
                      End meeting
                    </button>
                  )}
                </div>

                {current ? (
                  <div className="rounded-md border border-gray-200 p-3">
                    <div className="flex gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                          {current.changeNumber}
                          {current.wasSkipped ? (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-medium">
                              Skipped
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-gray-800">
                          {current.shortDescription}
                        </p>
                        <dl className="mt-2 space-y-0.5 text-xs text-gray-600">
                          <div>
                            <span className="text-gray-500">Assignment group: </span>
                            {current.assignmentGroupName || '\u2014'}
                          </div>
                          <div>
                            <span className="text-gray-500">Assigned to: </span>
                            {current.assignedToName || '\u2014'}
                          </div>
                          <div>
                            <span className="text-gray-500">State: </span>
                            <span
                              className={
                                current.cabDecision === 'approved'
                                  ? 'text-green-700 font-medium'
                                  : current.cabDecision === 'rejected'
                                    ? 'text-red-700 font-medium'
                                    : undefined
                              }
                            >
                              {current.changeState || '\u2014'}
                            </span>
                          </div>
                        </dl>
                      </div>
                      <div className="shrink-0 flex flex-col items-stretch gap-2 min-w-[5.5rem]">
                        {meetingActive && current.canCabApprove ? (
                          <>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setShowApproveModal(true)}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-md disabled:opacity-50 ${TEAL.btn}`}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setShowRejectModal(true)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-700 text-gray-900 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        ) : null}
                        {meetingActive &&
                        (current.canCabSkip || meeting.canHostMeeting) ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setShowSkipModal(true)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-700 text-gray-900 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            CR Skip
                          </button>
                        ) : null}
                        <div className="flex flex-col items-center">
                          <div className="h-12 w-12 rounded-full border-2 border-teal-600 text-teal-800 flex items-center justify-center text-sm font-semibold">
                            {Math.ceil(currentRemainingSeconds / 60)}
                          </div>
                          <div className="mt-1 text-[10px] text-gray-500 text-center leading-tight max-w-[4.5rem]">
                            Scheduled {current.allottedMinutes} minutes
                          </div>
                        </div>
                      </div>
                    </div>

                    {meeting.canHostMeeting && (
                      <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                        <div className="flex items-center gap-3">
                          {current.status === 'paused' ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                runAction(() => resumeMutation.mutateAsync(current.id))
                              }
                              className={`${TEAL.link} disabled:opacity-50`}
                            >
                              Resume
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                runAction(() => pauseMutation.mutateAsync(current.id))
                              }
                              className={`${TEAL.link} disabled:opacity-50`}
                            >
                              Pause
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              runAction(() => nextMutation.mutateAsync(current.id))
                            }
                            className={`${TEAL.link} disabled:opacity-50`}
                          >
                            Next
                          </button>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            runAction(() => demoteMutation.mutateAsync(current.id))
                          }
                          className={`${TEAL.link} disabled:opacity-50`}
                        >
                          Demote
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500 text-center">
                    No pending agenda items remaining.
                    {meeting.showEndMeeting && (
                      <div className="mt-3">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={handleEndMeeting}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md ${TEAL.btn}`}
                        >
                          End meeting
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
                <select
                  className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                  value={agendaListFilter}
                  onChange={(e) =>
                    setAgendaListFilter(e.target.value as AgendaListFilter)
                  }
                  aria-label="Agenda filter"
                >
                  <option value="pending">Pending Agenda Items</option>
                  <option value="all">All Agenda Items</option>
                </select>
                <div className="inline-flex border border-gray-300 rounded-md overflow-hidden">
                  <span className="p-1.5 text-gray-400">
                    <ChevronLeftIcon className="h-4 w-4" />
                  </span>
                  <span className="p-1.5 text-gray-400 border-l border-gray-300">
                    <ChevronUpIcon className="h-4 w-4" />
                  </span>
                  <span className="p-1.5 text-gray-400 border-l border-gray-300">
                    <ChevronRightIcon className="h-4 w-4" />
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {listedAgendaItems.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">
                    {agendaListFilter === 'all'
                      ? 'No agenda items available for this meeting.'
                      : 'No pending agenda items remaining.'}
                  </p>
                ) : (
                  listedAgendaItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-md border border-gray-200 p-3 bg-white"
                    >
                      <div className="flex gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-semibold text-gray-900">
                              {item.changeNumber}
                            </div>
                            {item.wasSkipped &&
                              (item.status === 'pending' ||
                                item.status === 'in_progress' ||
                                item.status === 'paused') && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                                Skipped
                              </span>
                            )}
                            {agendaListFilter === 'all' && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                {agendaStatusLabel(item.status)}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-gray-700 line-clamp-2">
                            {item.shortDescription}
                          </p>
                        </div>
                        <div className="shrink-0 h-9 w-9 rounded-full bg-gray-100 border flex items-center justify-center text-sm font-medium text-gray-700">
                          {item.allottedMinutes}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="border-t border-gray-200 flex flex-col min-h-[140px] max-h-[40%] shrink-0">
                <MeetingNotesPanel
                  notes={meeting.meetingNotes}
                  notesByChange={meeting.meetingNotesByChange}
                  compact
                />
              </div>
            </aside>

            {/* Right: CR details */}
            <section className="min-h-0 overflow-hidden">
              <CabWorkbenchChangePanel
                meetingId={meeting.id}
                changeRequestId={current?.changeRequestId}
              />
            </section>
          </div>
        )}
      </div>

      <ChangeApprovalDecisionModal
        isOpen={showApproveModal}
        decision="approved"
        isSubmitting={cabDecisionMutation.isPending}
        onClose={() => {
          if (!cabDecisionMutation.isPending) setShowApproveModal(false);
        }}
        onSubmit={handleApproveSubmit}
      />

      <ChangeApprovalDecisionModal
        isOpen={showRejectModal}
        decision="rejected"
        isSubmitting={cabDecisionMutation.isPending}
        onClose={() => {
          if (!cabDecisionMutation.isPending) setShowRejectModal(false);
        }}
        onSubmit={handleRejectSubmit}
      />

      <ChangeApprovalDecisionModal
        isOpen={showSkipModal}
        decision="skipped"
        changeNumber={current?.changeNumber}
        isSubmitting={skipMutation.isPending}
        onClose={() => {
          if (!skipMutation.isPending) setShowSkipModal(false);
        }}
        onSubmit={handleSkipSubmit}
      />
    </div>
  );
};

function MeetingNotesPanel({
  notes,
  notesByChange,
  noteEntries,
  compact,
  chronological,
}: {
  notes?: string | null;
  notesByChange?: CabWorkbenchMeetingNotesGroup[];
  noteEntries?: CabWorkbenchMeetingNoteEntry[];
  compact?: boolean;
  /** Prefer chronological note lines (completed meeting center panel). */
  chronological?: boolean;
}) {
  const grouped = notesByChange ?? [];
  const entries = noteEntries ?? [];
  const lines = (notes || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const chronologicalLines =
    entries.length > 0
      ? entries.flatMap((entry) =>
          (entry.line || '')
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
        )
      : lines;

  const useChronological = Boolean(chronological) || (compact === false && entries.length > 0);
  const showChronological = useChronological && chronologicalLines.length > 0;
  const showGrouped = !showChronological && grouped.length > 0;

  const entryCount = showChronological
    ? entries.length > 0
      ? entries.length
      : chronologicalLines.length
    : showGrouped
      ? grouped.reduce(
          (sum, g) =>
            sum +
            (g.approvedComment ? 1 : 0) +
            (g.rejectedComment ? 1 : 0) +
            g.otherEntries.length,
          0
        )
      : lines.length;

  return (
    <div className={`flex flex-col min-h-0 ${compact ? 'h-full' : 'h-full'}`}>
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between shrink-0">
        <h3 className="text-sm font-semibold text-gray-900">Meeting Notes</h3>
        <span className="text-xs text-gray-500">
          {entryCount} entr{entryCount === 1 ? 'y' : 'ies'}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {showChronological ? (
          <div className="space-y-2 font-mono text-xs sm:text-sm text-gray-800">
            {chronologicalLines.map((line, idx) => (
              <div key={`${idx}-${line.slice(0, 48)}`} className="break-words whitespace-pre-wrap">
                {line}
              </div>
            ))}
          </div>
        ) : showGrouped ? (
          <div className="space-y-4 text-sm text-gray-800">
            {grouped.map((group) => (
              <div key={group.changeNumber} className="border-b border-gray-100 pb-3 last:border-0">
                <p className="font-semibold text-gray-900">CR: {group.changeNumber}</p>
                {group.approvedComment ? (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-green-800">Approved Comment:</p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words">{group.approvedComment}</p>
                  </div>
                ) : null}
                {group.rejectedComment ? (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-red-800">Rejected Comment:</p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words">{group.rejectedComment}</p>
                  </div>
                ) : null}
                {group.otherEntries.map((entry) => (
                  <div key={entry.id} className="mt-2 font-mono text-xs break-words whitespace-pre-wrap">
                    {entry.line}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : lines.length === 0 ? (
          <p className="text-sm text-gray-500">
            No meeting notes yet. Notes are added automatically when a Change Request receives an
            approval decision.
          </p>
        ) : (
          <div className="space-y-2 font-mono text-xs sm:text-sm text-gray-800">
            {lines.map((line, idx) => (
              <div key={`${idx}-${line.slice(0, 40)}`} className="break-words">
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttendeesTable({
  meeting,
}: {
  meeting: { attendees: Array<{
    id: string;
    attendeeName: string;
    reason: string;
    attendanceStatus: string;
    inviteSent: boolean;
  }> };
}) {
  if (meeting.attendees.length === 0) {
    return <p className="text-sm text-gray-500">No attendees for this meeting.</p>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Attendee
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Reason
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Attendance
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Invite Sent
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {meeting.attendees.map((a) => (
          <tr key={a.id}>
            <td className="px-3 py-2">{a.attendeeName}</td>
            <td className="px-3 py-2">{getAttendeeReasonLabel(a.reason)}</td>
            <td className="px-3 py-2">{getAttendanceStatusLabel(a.attendanceStatus)}</td>
            <td className="px-3 py-2">{a.inviteSent ? 'True' : 'False'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ConferenceBlock({
  meeting,
}: {
  meeting: {
    conferenceDetails?: string;
    conferenceType?: string;
    meetingSubject?: string;
    teamsMeetingUrl?: string;
    teamsMeetingId?: string;
    teamsPasscode?: string;
    scheduledStart: string;
    scheduledEnd: string;
  };
}) {
  const hasStructured =
    Boolean(meeting.conferenceType) ||
    Boolean(meeting.meetingSubject) ||
    Boolean(meeting.teamsMeetingUrl) ||
    Boolean(meeting.teamsMeetingId) ||
    Boolean(meeting.teamsPasscode);

  const hasLegacy = Boolean(meeting.conferenceDetails?.trim());

  if (!hasStructured && !hasLegacy) {
    return (
      <div className="max-w-3xl">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Conference Information</h2>
        <p className="text-sm text-gray-500">
          No conference information has been configured for this meeting.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <h2 className="text-sm font-semibold text-gray-900">Conference Information</h2>
      {hasStructured ? (
        <dl className="border border-gray-200 rounded-md divide-y divide-gray-100 text-sm">
          {meeting.conferenceType ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-gray-500">Conference Type</dt>
              <dd className="col-span-2 text-gray-900">{meeting.conferenceType}</dd>
            </div>
          ) : null}
          {meeting.meetingSubject ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-gray-500">Meeting Subject</dt>
              <dd className="col-span-2 text-gray-900">{meeting.meetingSubject}</dd>
            </div>
          ) : null}
          <div className="grid grid-cols-3 gap-2 px-4 py-2">
            <dt className="text-gray-500">Start Date/Time</dt>
            <dd className="col-span-2 text-gray-900">
              {new Date(meeting.scheduledStart).toLocaleString()}
            </dd>
          </div>
          <div className="grid grid-cols-3 gap-2 px-4 py-2">
            <dt className="text-gray-500">End Date/Time</dt>
            <dd className="col-span-2 text-gray-900">
              {new Date(meeting.scheduledEnd).toLocaleString()}
            </dd>
          </div>
          {meeting.teamsMeetingUrl ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-gray-500">Teams Join Link</dt>
              <dd className="col-span-2">
                <a
                  href={meeting.teamsMeetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline break-all"
                >
                  {meeting.teamsMeetingUrl}
                </a>
              </dd>
            </div>
          ) : null}
          {meeting.teamsMeetingId ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-gray-500">Meeting ID</dt>
              <dd className="col-span-2 text-gray-900">{meeting.teamsMeetingId}</dd>
            </div>
          ) : null}
          {meeting.teamsPasscode ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-2">
              <dt className="text-gray-500">Passcode</dt>
              <dd className="col-span-2 text-gray-900">{meeting.teamsPasscode}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {hasLegacy && !hasStructured ? (
        <div className="border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800">
          {meeting.conferenceDetails}
        </div>
      ) : null}
    </div>
  );
}

export default CabWorkbenchMeetingDetail;
